import Button from "atoms/buttons/button";
import Heading from "atoms/typography/heading";
import Icon from "atoms/icons/icon";
import Modal from "molecules/modals/modal";
import React, { useEffect, useMemo, useState } from "react";
import TextInput from "atoms/forms/text-input";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { InputTypes } from "atoms/constants/input-types";
import { ModalTypes } from "molecules/constants/modal-types";
import { useLocalization } from "utilities/hooks/use-localization";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import PageRecord from "models/view-models/page-record";
import useClonePages from "utilities/hooks/domain/clone-pages/use-clone-pages";
import StringUtils from "utilities/string-utils";
import { useHistory } from "react-router-dom";
import { RouteUtils } from "utilities/route-utils";
import { siteMap } from "internal-sitemap";
import { ToastManager } from "utilities/toast/toast-manager";
import NotebookRecord from "models/view-models/notebook-record";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import SelectExistingOrNewNotebookInput from "../../select-existing-or-new-notebook-input/select-existing-or-new-notebook-input";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import Loader from "atoms/loaders/loader";
import { CollectionUtils } from "utilities/collection-utils";

interface CopyPageModalProps {
    onSuccess?: (clonedPage: any) => void;
    onError?: (errors: string[]) => void;
    page: PageRecord | null;
    isVisible: boolean;
    onClose: () => void;
}

const CSS_CLASS_NAME: string = "c-copy-page-modal";

export const CopyPageModal: React.FC<CopyPageModalProps> = ({
    onSuccess,
    onError,
    page,
    isVisible = false,
    onClose,
}: CopyPageModalProps): JSX.Element => {
    const { t } = useLocalization();
    const history = useHistory();
    const {
        resultObject: notebooks,
        refresh: getAllNotebooks,
        createNotebook,
        addPageToNotebook,
    } = useNotebooksAtom();
    const { globalState } = useGlobalState();
    const currentUserId = globalState.currentIdentity?.userId();
    const availableNotebooks = notebooks.filter(
        (notebook) => notebook.id !== 0 && notebook.ownerId === currentUserId
    );
    const { refresh: refreshNotebooksAtom } = useNotebooksAtom();
    const [pageName, setPageName] = useState<string>("");
    const [selectedNotebook, setSelectedNotebook] = useState<number | string>(
        0
    );

    const isMobile = useBreakpoint(
        Breakpoints.SmallTablet,
        BreakpointComparer.MaxWidth
    );
    const modalType = isMobile ? ModalTypes.Bottom : ModalTypes.Base;

    const handleCopyPageSuccess = (clonedPage: PageRecord) => {
        ToastManager.success(t("pageSuccessfullyCopied"));

        refreshNotebooksAtom();

        const route = RouteUtils.getUrl(siteMap.notebooks.notebook.page, {
            notebookId: clonedPage.notebookId,
            pageId: clonedPage.id,
        });

        if (route) {
            history.push(route);
        }
    };

    const { clonePage, loading } = useClonePages({
        onSuccess: (clonedPage) => {
            onClose?.();
            handleCopyPageSuccess(clonedPage);
            onSuccess?.(clonedPage);
        },
        onError: (errorMessages) => {
            onError?.(errorMessages);
            ToastManager.error(t("pageCopyFailed"));
        },
    });

    const isValidPage = page?.id != null && page.id > 0;
    const isValidPageName = StringUtils.hasValue(pageName?.trim());
    const isValidNotebookSelected = useMemo(() => {
        if (typeof selectedNotebook === "string") {
            return StringUtils.hasValue(selectedNotebook.trim());
        } else {
            return selectedNotebook > 0;
        }
    }, [selectedNotebook]);
    const canCopy = isValidPageName && isValidNotebookSelected && !loading;

    const createNewNotebook = async (
        newNotebookName: string
    ): Promise<number | null> => {
        if (!isValidNotebookSelected) {
            return null;
        }

        try {
            const notebook = await createNotebook(
                new NotebookRecord({
                    ownerId: currentUserId,
                    name: newNotebookName,
                })
            );

            if (notebook?.id) {
                setSelectedNotebook(notebook.id);
                return notebook.id;
            } else {
                throw new Error("Notebook creation failed");
            }
        } catch (error) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("creating"),
                    resource: t("notebook"),
                })
            );
            return null;
        }
    };

    const handlePageNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPageName(e.target.value);
    };

    const handleFormReset = () => {
        setPageName("");
        setSelectedNotebook(0);
        onClose?.();
    };

    const handleCopyClick = async () => {
        if (!isValidPageName || !isValidPage) {
            return;
        }

        let notebookId: number;

        if (typeof selectedNotebook === "string") {
            const createdNotebookId = await createNewNotebook(selectedNotebook);

            if (createdNotebookId == null) {
                return;
            }

            notebookId = createdNotebookId;
        } else {
            notebookId = selectedNotebook;
        }

        const clonedPage = await clonePage({
            name: pageName.trim(),
            notebookId: notebookId,
            sourcePageId: page.id,
        });

        addPageToNotebook(notebookId, clonedPage);
    };

    const handleSelectedNotebookChange = (
        selectedNotebook: number | string
    ) => {
        setSelectedNotebook(selectedNotebook);
    };

    useEffect(() => {
        const setNotebookFromPage = (page: PageRecord): void => {
            if (page.ownerId === currentUserId) {
                setSelectedNotebook(page.notebookId);
                return;
            }

            var ownedNotebookIds = notebooks
                ?.filter((n) => n.ownerId === currentUserId)
                .map((n) => n.id)
                .filter((id) => id != null);

            if (CollectionUtils.hasValues(ownedNotebookIds)) {
                setSelectedNotebook(ownedNotebookIds[0]);
                return;
            }

            setSelectedNotebook(0);
        };

        if (isVisible && page) {
            setPageName(page.name ? `${page.name} (${t("copy")})` : "");

            setNotebookFromPage(page);
        }
    }, [page, isVisible, currentUserId, notebooks, t]);

    useEffect(() => {
        if (isVisible && notebooks.length === 0) {
            getAllNotebooks();
        }
    }, [notebooks.length, getAllNotebooks, isVisible]);

    return (
        <Modal
            closeButtonStyle={ModalCloseButtonStyle.Hidden}
            closeDialog={onClose}
            cssClassName={CSS_CLASS_NAME}
            isVisible={isVisible}
            label={t("copy")}
            stopEventPropagation={true}
            type={modalType}>
            <Heading cssClassName={`${CSS_CLASS_NAME}__heading`}>
                {t("copy")} {StringUtils.capitalize(t("page"))}
                <Button
                    accessibleText={t("close")}
                    cssClassName={"-modal-close -icon"}
                    onClick={onClose}
                    size={ButtonSizes.Medium}
                    style={ButtonStyles.None}>
                    <Icon type={Icons.Close} size={IconSizes.Large} />
                </Button>
            </Heading>
            <div className={`${CSS_CLASS_NAME}__content`}>
                {loading ? (
                    <div className={`${CSS_CLASS_NAME}__loader-wrapper`}>
                        <Loader />
                    </div>
                ) : (
                    <>
                        <div className={`${CSS_CLASS_NAME}__form-field`}>
                            <label htmlFor="copy-page-name-input">
                                {t("name")}
                            </label>
                            <TextInput
                                id="copy-page-name-input"
                                cssClassName={`${CSS_CLASS_NAME}__page-name-input`}
                                type={InputTypes.Text}
                                value={pageName}
                                onChange={handlePageNameChange}
                                placeholder={t("name")}
                            />
                        </div>
                        <SelectExistingOrNewNotebookInput
                            availableNotebooks={availableNotebooks}
                            onSelectedNotebookChange={
                                handleSelectedNotebookChange
                            }
                            selectedNotebook={selectedNotebook}
                        />
                    </>
                )}
            </div>
            <div className={`${CSS_CLASS_NAME}__actions`}>
                <Button
                    accessibleText={t("cancel")}
                    onClick={handleFormReset}
                    style={ButtonStyles.Secondary}>
                    {t("cancel")}
                </Button>
                <Button
                    accessibleText={t("copy")}
                    disabled={!canCopy}
                    onClick={handleCopyClick}
                    style={ButtonStyles.Primary}>
                    {t("copy")}
                </Button>
            </div>
        </Modal>
    );
};
